import { useState, useEffect } from "react"
import { useGlobalContext } from "../../data/GlobalContext"
import { apiJSONURL } from "../../../App"

// Helper to format date into the "MMM DD, YYYY, hh:mm AM/PM" format
export const formatDate = (dateString) => {
  const date = new Date(dateString)
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }
  return date.toLocaleString("en-US", options)
}

export default function TransactionHistory() {
  const { transactionReport, filterTransactions, username } = useGlobalContext()

  // Generate default dates
  // const today = new Date().toISOString().split("T")[0]
  // const defaultFromDate = new Date().toISOString().split("T")[0]

  // State for the date filters with default values
  const [fromDate, setFromDate] = useState(
    () => new Date().toISOString().split("T")[0]
  )
  const [toDate, setToDate] = useState(
    () => new Date().toISOString().split("T")[0]
  )
  const [filterType, setFilterType] = useState("all")
  const [filterStatus, setFilterStatus] = useState("all")

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // Helper function to convert numeric status to text.
  const formatStatus = (status) => {
    switch (status) {
      case 1:
        return "Success"
      case 0:
        return "Failed"
      case -1:
        return "Pending"
      case 2:
        return "In Processing"
      case -2:
        return "All"
      case 4:
        return "Success Confirmed"
      case 5:
        return "Success & Transfered"
      default:
        return status
    }
  }

  // Filter handling that resets to page 1 after filtering
  const handleFilter = async () => {
    await filterTransactions(fromDate, toDate, filterType, filterStatus)
    setCurrentPage(1)
  }

  const cancelPendingDeposit = async (username, transactionId) => {
    try {
      const response = await fetch(apiJSONURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `cmd=cancelPendingDeposit&u=${encodeURIComponent(
          username
        )}&id=${encodeURIComponent(transactionId)}`,
      })

      if (!response.ok) {
        throw new Error(`Failed to cancel deposit: ${response.statusText}`)
      }
      handleFilter()
      const data = await response.json()
      console.log("Deposit cancelled:", data)
      return data
    } catch (error) {
      console.error("Error cancelling deposit:", error)
      throw error
    }
  }

  // Run filter on component mount if user exists
  useEffect(() => {
    if (username) {
      handleFilter()
    }
  }, [username])

  // Compute pagination details
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentTransactions = transactionReport.slice(
    indexOfFirstItem,
    indexOfLastItem
  )
  const totalPages = Math.ceil(transactionReport.length / itemsPerPage)

  useEffect(() => {
    if (!username) return

    const getPhilippineDate = () => {
      const now = new Date()
      const phTime = new Date(now.getTime() + 8 * 60 * 60 * 1000)
      return phTime.toISOString().split("T")[0]
    }

    const refreshData = () => {
      const phToday = getPhilippineDate()
      setToDate((prevToDate) => {
        if (prevToDate !== phToday) {
          setFromDate(phToday)
          setToDate(phToday)
        }
        return phToday
      })
      handleFilter()
    }

    refreshData()
    const interval = setInterval(refreshData, 60 * 1000)

    return () => clearInterval(interval)
  }, [username, filterType, filterStatus])

  return (
    <div className="transaction-report-container">
      <div className="transaction-report-tools">
        <div className="transaction-report-datepicker">
          <label>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="data-datepicker"
          />
        </div>
        <div className="transaction-report-datepicker">
          <label>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="data-datepicker"
          />
        </div>
        <div className="transaction-report-filter">
          <label>Type of Request</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="transction-report-filter-selection"
          >
            <option value="all">All</option>
            <option value="deposit">Deposit</option>
            <option value="withdrawal">Withdrawal</option>
          </select>
        </div>
        <div className="transaction-report-filter">
          <label>Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="transction-report-filter-selection"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <button
          onClick={handleFilter}
          className="transaction-report-filter-btn"
        >
          Continue
        </button>
      </div>
      <div className="transaction-report-wrapper">
        <div className="transaction-report-table">
          <div className="transaction-report-head">
            <th>Date</th>
            <th>Status</th>
            <th>Type Ref. No</th>
            <th>Bank</th>
            <th>E-WALLET</th>
            <th>Amount</th>
            <th>Bonus</th>
          </div>
          <div className="transaction-report-body">
            {currentTransactions.length === 0 ? (
              <div className="no-data-message">No data available</div>
            ) : (
              currentTransactions.map((tx, index) => (
                <div key={index} className="transaction-report-results">
                  <div>{formatDate(tx.dateTime)}</div>
                  <div>
                    {formatStatus(tx.status)}
                    {tx.status === -1 &&
                      !tx.transactionSummary
                        .toLowerCase()
                        .includes("withdrawal") && (
                        <button
                          className="cancel-deposit"
                          onClick={() =>
                            cancelPendingDeposit(username, tx.transacId)
                          }
                        >
                          Cancel
                        </button>
                      )}
                  </div>
                  <div>{tx.transactionSummary}</div>
                  <div>{tx.vendor}</div>
                  <div>{tx.paymentMethod}</div>
                  <div>{tx.amount}</div>
                  <div>{tx.bonus}</div>
                </div>
              ))
            )}
            {transactionReport.length > 0 && (
              <div className="transaction-report-results total-row">
                <div></div> {/* Date */}
                <div></div> {/* Status */}
                <div></div> {/* Type Ref. No */}
                <div></div> {/* Bank */}
                <div></div> {/* E-WALLET */}
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {transactionReport
                    .filter((tx) => {
                      if (filterType === "deposit") {
                        return tx.transactionSummary
                          ?.toLowerCase()
                          .includes("deposit")
                      }
                      if (filterType === "withdrawal") {
                        return tx.transactionSummary
                          ?.toLowerCase()
                          .includes("withdrawal")
                      }
                      return true // if filterType is "all"
                    })
                    .reduce((total, tx) => total + Number(tx.amount || 0), 0)
                    .toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {transactionReport.length > 0 && (
        <div className="pagination" style={{ marginBottom: "190px" }}>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {fromDate === "" && toDate === "" && (
        <div className="game-report-disclaimer-message">
          ( Data's from about a week ago—pick a date to see more )
        </div>
      )}
    </div>
  )
}
